/**
 * Espace formateur (Rafik 02/06/2026, Phase 3).
 *
 * Sous-page dédiée à 1 formateur : ses séances + KPIs spécifiques
 * (heures cumulées synchrones/asynchrones, satisfaction moyenne, nb élèves
 * distincts émargés).
 *
 * URL : /emargements/formateur/:id
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ChevronLeft,
  PenTool,
  Clock,
  CheckCircle2,
  Users,
  Star,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import type { SeanceDTO } from './EmargementsPage.js';

const API_BASE =
  typeof window !== 'undefined' && window.location.host.startsWith('cockpit-v2.')
    ? 'https://cockpit.andalous-elearning.com/api/v1'
    : '/api/v1';

const STATUS_LABEL: Record<SeanceDTO['status'], string> = {
  pending: 'En attente prof',
  awaiting_signature: 'En attente signature',
  submitted: 'Signée',
  expired: 'Expirée',
};
const STATUS_COLOR: Record<SeanceDTO['status'], string> = {
  pending: 'bg-amber-100 text-amber-700',
  awaiting_signature: 'bg-sky-100 text-sky-700',
  submitted: 'bg-emerald-100 text-emerald-700',
  expired: 'bg-rose-100 text-rose-700',
};

export function EspaceFormateurPage() {
  const { id } = useParams<{ id: string }>();
  const [seances, setSeances] = useState<SeanceDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formateurName, setFormateurName] = useState<string>('');

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    fetch(`${API_BASE}/v2/emargement/seances?formateurZohoId=${encodeURIComponent(id)}`, {
      credentials: 'include',
    })
      .then((r) => r.json())
      .then(
        (json: { data: { seances: SeanceDTO[] } | null; error: { message?: string } | null }) => {
          if (!json.data) throw new Error(json.error?.message ?? 'Réponse invalide');
          const list = json.data.seances ?? [];
          setSeances(list);
          // Récupère le nom formateur depuis la 1ère séance
          if (list[0]?.destinataireNom) setFormateurName(list[0].destinataireNom);
          setIsLoading(false);
        },
      )
      .catch((e) => {
        setError((e as Error).message);
        setIsLoading(false);
      });
  }, [id]);

  const kpis = useMemo(() => {
    const total = seances.length;
    const signees = seances.filter((s) => s.status === 'submitted').length;
    const elevesDistincts = new Set(seances.map((s) => s.apprenantZohoId)).size;

    // Heures cumulées (depuis les réponses prof)
    const heuresTotal = seances.reduce((sum, s) => {
      const d = s.answers?.['duree_heures'];
      const v = typeof d === 'number' ? d : Number(d ?? 0);
      return sum + (isNaN(v) ? 0 : v);
    }, 0);

    // Satisfaction moyenne (depuis les réponses élève)
    const satNotes = seances
      .map((s) => s.secondaryAnswers?.['satisfaction'])
      .map((n) => (typeof n === 'number' ? n : Number(n)))
      .filter((n) => !isNaN(n) && n > 0);
    const satMoy =
      satNotes.length > 0 ? satNotes.reduce((a, b) => a + b, 0) / satNotes.length : null;

    return { total, signees, elevesDistincts, heuresTotal, satMoy, nbAvis: satNotes.length };
  }, [seances]);

  // Liste des commentaires élève
  const avis = useMemo(() => {
    return seances
      .filter((s) => s.status === 'submitted')
      .map((s) => ({
        id: s.id,
        date: s.secondarySubmittedAt?.slice(0, 10) ?? '—',
        eleve: s.secondaryDestinataireNom ?? '—',
        satisfaction: s.secondaryAnswers?.['satisfaction'],
        commentaire: s.secondaryAnswers?.['commentaire_eleve'] ?? '',
      }))
      .filter((a) => a.satisfaction || a.commentaire);
  }, [seances]);

  if (!id) return null;

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      <Link
        to="/emargements"
        className="mb-4 inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-[#0a2540]"
      >
        <ChevronLeft className="h-4 w-4" />
        Retour aux émargements
      </Link>

      <header className="mb-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-neutral-900">
          <PenTool className="h-6 w-6 text-[#c89b3c]" />
          Espace formateur — {formateurName || 'Chargement…'}
        </h1>
        <p className="mt-1 text-sm text-neutral-500">
          Activité, signatures et avis élèves de ce formateur.
        </p>
      </header>

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 p-12 text-sm text-neutral-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Chargement…
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          <AlertCircle className="h-4 w-4" />
          Erreur : {error}
        </div>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-5 gap-4">
            <Kpi label="Séances" value={kpis.total} icon={<PenTool className="h-4 w-4" />} />
            <Kpi
              label="Signées"
              value={kpis.signees}
              icon={<CheckCircle2 className="h-4 w-4" />}
              color="emerald"
            />
            <Kpi
              label="Élèves émargés"
              value={kpis.elevesDistincts}
              icon={<Users className="h-4 w-4" />}
            />
            <Kpi
              label="Heures cumulées"
              value={`${kpis.heuresTotal.toFixed(1)} h`}
              icon={<Clock className="h-4 w-4" />}
            />
            <Kpi
              label={`Satisfaction (${kpis.nbAvis} avis)`}
              value={kpis.satMoy !== null ? `${kpis.satMoy.toFixed(1)} / 5` : '—'}
              icon={<Star className="h-4 w-4" />}
              color="amber"
            />
          </div>

          {/* Tableau séances */}
          <section className="mb-8">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-neutral-700">
              Toutes les séances
            </h2>
            <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
              {seances.length === 0 ? (
                <div className="p-8 text-center text-sm text-neutral-500">
                  Aucune séance pour ce formateur.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="border-b border-neutral-200 bg-neutral-50">
                    <tr className="text-left text-xs font-semibold uppercase tracking-wider text-neutral-600">
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Élève</th>
                      <th className="px-4 py-3">Durée</th>
                      <th className="px-4 py-3">Statut</th>
                      <th className="px-4 py-3">Note</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {seances.map((s) => {
                      const dateSeance =
                        typeof s.answers?.date_seance === 'string'
                          ? s.answers.date_seance
                          : s.issuedAt.slice(0, 10);
                      const duree = s.answers?.['duree_heures'];
                      const sat = s.secondaryAnswers?.['satisfaction'];
                      return (
                        <tr key={s.id} className="hover:bg-neutral-50">
                          <td className="px-4 py-3 font-mono text-xs">{dateSeance}</td>
                          <td className="px-4 py-3">{s.secondaryDestinataireNom ?? '—'}</td>
                          <td className="px-4 py-3 text-neutral-700">
                            {duree ? `${duree} h` : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex rounded px-2 py-0.5 text-[11px] font-semibold ${STATUS_COLOR[s.status]}`}
                            >
                              {STATUS_LABEL[s.status]}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-neutral-700">
                            {typeof sat === 'number' || (sat && !isNaN(Number(sat)))
                              ? `${sat} / 5`
                              : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          {/* Avis élèves */}
          <section>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-neutral-700">
              Avis des élèves ({avis.length})
            </h2>
            {avis.length === 0 ? (
              <div className="rounded-lg border border-neutral-200 bg-white p-6 text-center text-sm text-neutral-500">
                Aucun avis élève pour ce formateur encore.
              </div>
            ) : (
              <div className="space-y-3">
                {avis.map((a) => (
                  <div key={a.id} className="rounded-lg border border-neutral-200 bg-white p-4">
                    <div className="mb-2 flex items-center justify-between">
                      <div className="text-sm font-semibold text-neutral-900">{a.eleve}</div>
                      <div className="flex items-center gap-2 text-xs text-neutral-500">
                        <span>{a.date}</span>
                        {a.satisfaction !== undefined && (
                          <span className="flex items-center gap-0.5 text-amber-600">
                            {[1, 2, 3, 4, 5].map((n) => (
                              <Star
                                key={n}
                                className={`h-3 w-3 ${n <= Number(a.satisfaction) ? 'fill-amber-400' : ''}`}
                              />
                            ))}
                          </span>
                        )}
                      </div>
                    </div>
                    {a.commentaire ? (
                      <p className="text-sm italic text-neutral-700">« {String(a.commentaire)} »</p>
                    ) : (
                      <p className="text-xs text-neutral-400">Pas de commentaire écrit.</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function Kpi({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color?: 'emerald' | 'amber';
}) {
  const ring =
    color === 'emerald'
      ? 'border-emerald-200 bg-emerald-50'
      : color === 'amber'
        ? 'border-amber-200 bg-amber-50'
        : 'border-neutral-200 bg-white';
  return (
    <div className={`rounded-lg border p-4 ${ring}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-neutral-600">
          {label}
        </span>
        <span className="text-neutral-400">{icon}</span>
      </div>
      <div className="mt-1 text-xl font-bold text-neutral-900">{value}</div>
    </div>
  );
}
